import { useEffect, useState } from "react";
import { businessesApi, integrationsApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Plug, Check, AlertCircle, RefreshCcw, Loader2 } from "lucide-react";

const XERO_LOGO = null;
const ZOHO_LOGO = null;

export default function Integrations() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [status, setStatus] = useState({ xero_configured: false, zoho_configured: false });
  const [params] = useSearchParams();

  const load = () => businessesApi.list().then(setBusinesses);

  useEffect(() => {
    load();
    integrationsApi.status().then(setStatus);
    if (params.get("connected")) toast.success(`Connected to ${params.get("connected")}`);
    if (params.get("error")) toast.error(params.get("error"));
  }, []);

  const connect = async (biz, provider) => {
    try {
      const res = await integrationsApi.authorize(provider, biz.id);
      window.location.href = res.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Not configured"); }
  };

  const disconnect = async (biz) => {
    if (!window.confirm("Disconnect this business from accounting?")) return;
    await integrationsApi.disconnect(biz.id);
    toast.success("Disconnected");
    load();
  };

  const setZohoOrg = async (biz) => {
    const org = window.prompt("Zoho organization_id:");
    if (!org) return;
    await integrationsApi.setZohoOrg(biz.id, org);
    toast.success("Zoho org saved");
    load();
  };

  const [syncing, setSyncing] = useState({});
  const masterSync = async (biz) => {
    setSyncing((s) => ({ ...s, [biz.id]: true }));
    try {
      const r = await integrationsApi.masterSync(biz.id);
      const parts = Object.entries(r.results).map(([k, v]) =>
        v.ok ? `${k}: +${v.created}/~${v.updated} of ${v.total}` : `${k}: ${v.error}`
      );
      if (r.ok) {
        toast.success(`Synced ${r.synced}/${r.total_steps} — ${parts.join(" · ")}`, { duration: 6000 });
      } else {
        toast.warning(`Partial sync (${r.synced}/${r.total_steps}) — ${parts.join(" · ")}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Master sync failed");
    } finally {
      setSyncing((s) => ({ ...s, [biz.id]: false }));
    }
  };

  const visible = isMaster(user) ? businesses : businesses.filter((b) => userBusinessIds(user).includes(b.id));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-4xl font-bold text-espresso" data-testid="integrations-title">Accounting integrations</h1>
        <p className="text-espresso-ink/60 mt-2">Connect each business to Xero or Zoho Books. DocHub only syncs as <b>Draft Bills</b> — never approved automatically.</p>
      </div>

      {(!status.xero_configured || !status.zoho_configured) && (
        <div className="p-4 bg-status-pending/10 border border-status-pending/30 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-status-pending mt-0.5" />
          <div className="text-sm text-espresso-ink">
            {!status.xero_configured && <div><b>Xero</b> client credentials missing — paste <code>XERO_CLIENT_ID</code> &amp; <code>XERO_CLIENT_SECRET</code> in <code>backend/.env</code> and restart backend.</div>}
            {!status.zoho_configured && <div><b>Zoho Books</b> client credentials missing — paste <code>ZOHO_CLIENT_ID</code> &amp; <code>ZOHO_CLIENT_SECRET</code> in <code>backend/.env</code> and restart backend.</div>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {visible.map((b) => {
          const providerConfigured = b.accounting === "xero" ? status.xero_configured : status.zoho_configured;
          const providerLogo = b.accounting === "xero" ? XERO_LOGO : ZOHO_LOGO;
          return (
            <div key={b.id} className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft" data-testid={`biz-card-${b.code}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="font-display text-lg font-bold text-espresso">{b.name}</div>
                  <div className="text-xs uppercase tracking-widest text-espresso-ink/50 mt-1">Code: {b.code}</div>
                </div>
                <div className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest ${b.accounting === "xero" ? "bg-[#13B5EA]/10 text-[#0A7FA5]" : "bg-[#E42527]/10 text-[#B01E1F]"}`} data-testid={`biz-provider-badge-${b.code}`}>
                  {b.accounting === "xero" ? "Xero" : "Zoho Books"}
                </div>
              </div>
              <div className="flex items-center gap-2 mb-4">
                {b.connected ? (
                  <span className="inline-flex items-center gap-1.5 text-status-synced text-xs font-semibold">
                    <Check className="w-3.5 h-3.5" /> Connected
                  </span>
                ) : (
                  <span className="text-xs text-espresso-ink/50 font-medium">Not connected</span>
                )}
                {b.accounting === "zoho" && b.zoho_org_id && <span className="text-xs text-espresso-ink/60">org: {b.zoho_org_id}</span>}
                {b.accounting === "xero" && b.xero_tenant_id && <span className="text-xs text-espresso-ink/60 truncate max-w-[180px]">tenant: {b.xero_tenant_id}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {b.connected ? (
                  <>
                    <button
                      data-testid={`master-sync-${b.code}`}
                      onClick={() => masterSync(b)}
                      disabled={!!syncing[b.id]}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-espresso text-canvas rounded-lg text-sm font-semibold hover:bg-espresso-light disabled:opacity-60"
                      title="Pull chart of accounts, vendors, VAT codes and items in one shot"
                    >
                      {syncing[b.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                      Master sync
                    </button>
                    <button data-testid={`disconnect-${b.code}`} onClick={() => disconnect(b)} className="px-4 py-2 border border-canvas-border text-espresso rounded-lg text-sm font-semibold hover:bg-canvas-border/50">Disconnect</button>
                  </>
                ) : (
                  <button data-testid={`connect-${b.code}`} onClick={() => connect(b, b.accounting)} disabled={!providerConfigured} className="inline-flex items-center gap-2 px-4 py-2 bg-espresso text-canvas rounded-lg text-sm font-semibold hover:bg-espresso-light disabled:opacity-50">
                    <Plug className="w-3.5 h-3.5" /> Connect {b.accounting === "xero" ? "Xero" : "Zoho"}
                  </button>
                )}
                {b.accounting === "zoho" && b.connected && (
                  <button onClick={() => setZohoOrg(b)} className="px-4 py-2 border border-canvas-border rounded-lg text-sm font-semibold text-espresso hover:bg-canvas-border/50">Set org ID</button>
                )}
              </div>
              {b.connected && (
                <div className="mt-3 text-[11px] text-espresso-ink/50">
                  Master sync pulls <b>chart of accounts</b>, <b>vendors</b>, <b>VAT codes</b> and <b>items</b> from {b.accounting === "xero" ? "Xero" : "Zoho Books"}.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
